import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  Grid,
} from '@mui/material';
import {
  ArrowBack,
  CheckCircle,
  Cancel,
  Block,
  LocalShipping,
  FactCheck,
  ReceiptLong,
  TaskAlt,
} from '@mui/icons-material';
import {
  PageHeader,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  StatusTone,
  TypePill,
  TableEmptyState,
} from '../../components/common/pageKit';
import { formatIndianNumber } from '../../utils/currency';
import AttachmentsPanel from '../../components/common/AttachmentsPanel';
import {
  salesReturnService,
  SalesReturnDetail,
  ReturnStatus,
} from '../../services/salesReturnService';

// ---------------------------------------------------------------------------
// Sales Return 360: header + lifecycle ribbon, returned items, and the
// contextual workflow actions (Approve / Receive / Inspect / Accept / Issue
// Credit Note / Reject / Cancel). Mirrors SalesContractDetailPage.tsx's
// overall shape.
// ---------------------------------------------------------------------------

const FLOW_STEPS: { key: string; label: string }[] = [
  { key: 'REQUESTED', label: 'Requested' },
  { key: 'APPROVED', label: 'Approved' },
  { key: 'RECEIVED', label: 'Received' },
  { key: 'INSPECTED', label: 'Inspected' },
  { key: 'ACCEPTED', label: 'Accepted' },
  { key: 'CREDIT_NOTE_ISSUED', label: 'Credit Note Issued' },
  { key: 'CLOSED', label: 'Closed' },
];

const FLOW_INDEX: Partial<Record<ReturnStatus, number>> = {
  REQUESTED: 0,
  APPROVED: 1,
  RECEIVED: 2,
  INSPECTED: 3,
  ACCEPTED: 4,
  CREDIT_NOTE_ISSUED: 5,
  CLOSED: 6,
};

const SIDE_STATES: ReturnStatus[] = ['REJECTED', 'CANCELLED'];

const STATUS_TONE: Record<ReturnStatus, StatusTone> = {
  REQUESTED: 'warning',
  APPROVED: 'info',
  RECEIVED: 'info',
  INSPECTED: 'info',
  ACCEPTED: 'success',
  CREDIT_NOTE_ISSUED: 'success',
  CLOSED: 'neutral',
  REJECTED: 'error',
  CANCELLED: 'error',
};

const TYPE_TINT: Record<string, string> = {
  DAMAGED: '#dc2626',
  WRONG_ITEM: '#d97706',
  EXCESS: '#0284c7',
  QUALITY_ISSUE: '#7c3aed',
  CUSTOMER_REJECTION: '#be123c',
  OTHER: '#64748b',
};

const CONDITION_TONE: Record<string, StatusTone> = {
  GOOD: 'success',
  DAMAGED: 'warning',
  SCRAP: 'error',
};

const toNum = (v: number | string | null | undefined): number => {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : 0;
};

const fmtDate = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString('en-IN') : '—');

const SalesReturnDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [salesReturn, setSalesReturn] = useState<SalesReturnDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const { salesReturn: data } = await salesReturnService.getById(id);
      setSalesReturn(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load sales return');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !salesReturn) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/sales/returns')} sx={{ mb: 2 }}>
          Back to Returns
        </Button>
        <Alert severity="error">{error || 'Sales return not found'}</Alert>
      </Box>
    );
  }

  const isSideState = SIDE_STATES.includes(salesReturn.status);
  const flowIndex = FLOW_INDEX[salesReturn.status] ?? 0;

  const canApprove = salesReturn.status === 'REQUESTED';
  const canReject = salesReturn.status === 'REQUESTED';
  const canMarkReceived = salesReturn.status === 'APPROVED';
  const canMarkInspected = salesReturn.status === 'RECEIVED';
  const canAccept = salesReturn.status === 'INSPECTED';
  const canCancel = salesReturn.status === 'APPROVED' || salesReturn.status === 'RECEIVED' || salesReturn.status === 'INSPECTED';
  const canIssueCreditNote = salesReturn.status === 'ACCEPTED' && !salesReturn.credit_note_number;
  const canClose = salesReturn.status === 'CREDIT_NOTE_ISSUED';
  const noActionsAvailable =
    !canApprove && !canReject && !canMarkReceived && !canMarkInspected && !canAccept && !canCancel && !canIssueCreditNote && !canClose;

  const runAction = async (name: string, fn: () => Promise<void>) => {
    setActionLoading(name);
    setActionError(null);
    try {
      await fn();
    } catch (err: any) {
      setActionError(err.message || `Failed to ${name}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleApprove = () =>
    runAction('approve', async () => {
      await salesReturnService.approve(id!);
      setNotice('Return approved.');
      await load();
    });

  const handleReject = () =>
    runAction('reject', async () => {
      await salesReturnService.updateStatus(id!, 'REJECTED');
      setNotice('Return rejected.');
      await load();
    });

  const handleMarkReceived = () =>
    runAction('receive', async () => {
      await salesReturnService.updateStatus(id!, 'RECEIVED');
      setNotice('Return marked as received.');
      await load();
    });

  const handleMarkInspected = () =>
    runAction('inspect', async () => {
      await salesReturnService.updateStatus(id!, 'INSPECTED');
      setNotice('Return marked as inspected.');
      await load();
    });

  const handleAccept = () =>
    runAction('accept', async () => {
      await salesReturnService.accept(id!);
      setNotice('Return accepted. Restock has been posted where applicable.');
      await load();
    });

  const handleCancel = () =>
    runAction('cancel', async () => {
      await salesReturnService.updateStatus(id!, 'CANCELLED');
      setNotice('Return cancelled.');
      await load();
    });

  const handleIssueCreditNote = () =>
    runAction('credit-note', async () => {
      await salesReturnService.issueCreditNote(id!);
      setNotice('Credit note issued.');
      await load();
    });

  const handleClose = () =>
    runAction('close', async () => {
      await salesReturnService.updateStatus(id!, 'CLOSED');
      setNotice('Return closed.');
      await load();
    });

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/sales/returns')} sx={{ mb: 1 }}>
        Back to Returns
      </Button>

      <PageHeader
        title={salesReturn.return_number}
        qualifier={salesReturn.customer_name ? `· ${salesReturn.customer_name}` : undefined}
        subtitle={`Raised ${fmtDate(salesReturn.return_date)}${salesReturn.invoice_number ? ` · Against invoice ${salesReturn.invoice_number}` : ''}`}
        actions={<StatusDot label={salesReturn.status.replace(/_/g, ' ')} tone={STATUS_TONE[salesReturn.status]} />}
      />

      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {salesReturn.credit_note_number && (
        <Alert
          severity="success"
          icon={<ReceiptLong />}
          sx={{ mb: 2.5 }}
          action={salesReturn.credit_note_id ? (
            <Button color="inherit" size="small" onClick={() => navigate(`/finance/credit-debit-notes?id=${salesReturn.credit_note_id}&kind=credit`)}>
              Open Credit Note
            </Button>
          ) : undefined}
        >
          <Typography sx={{ fontWeight: 700 }}>
            Credit Note {salesReturn.credit_note_number} issued for this return.
          </Typography>
        </Alert>
      )}

      {/* Lifecycle ribbon */}
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        {isSideState ? (
          <>
            <Stepper activeStep={-1} alternativeLabel sx={{ opacity: 0.45, mb: 2 }}>
              {FLOW_STEPS.map((s) => (
                <Step key={s.key}>
                  <StepLabel>{s.label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            <Alert severity="warning" icon={salesReturn.status === 'REJECTED' ? <Block /> : <Cancel />}>
              <Typography sx={{ fontWeight: 700 }}>
                Return {salesReturn.status === 'REJECTED' ? 'Rejected' : 'Cancelled'}
              </Typography>
            </Alert>
          </>
        ) : (
          <Stepper activeStep={flowIndex} alternativeLabel>
            {FLOW_STEPS.map((s, i) => (
              <Step key={s.key} completed={i < flowIndex}>
                <StepLabel>{s.label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}
      </Paper>

      {/* Workflow actions */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
          Workflow Actions
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
          {canApprove && (
            <Button variant="contained" color="success" startIcon={<CheckCircle />} disabled={actionLoading !== null} onClick={handleApprove}>
              {actionLoading === 'approve' ? 'Approving...' : 'Approve'}
            </Button>
          )}
          {canReject && (
            <Button variant="outlined" color="error" startIcon={<Block />} disabled={actionLoading !== null} onClick={handleReject}>
              {actionLoading === 'reject' ? 'Rejecting...' : 'Reject'}
            </Button>
          )}
          {canMarkReceived && (
            <Button variant="contained" startIcon={<LocalShipping />} disabled={actionLoading !== null} onClick={handleMarkReceived}>
              {actionLoading === 'receive' ? 'Marking...' : 'Mark Received'}
            </Button>
          )}
          {canMarkInspected && (
            <Button variant="contained" startIcon={<FactCheck />} disabled={actionLoading !== null} onClick={handleMarkInspected}>
              {actionLoading === 'inspect' ? 'Marking...' : 'Mark Inspected'}
            </Button>
          )}
          {canAccept && (
            <Button variant="contained" color="success" startIcon={<TaskAlt />} disabled={actionLoading !== null} onClick={handleAccept}>
              {actionLoading === 'accept' ? 'Accepting...' : 'Accept (Restock)'}
            </Button>
          )}
          {canIssueCreditNote && (
            <Button variant="contained" startIcon={<ReceiptLong />} disabled={actionLoading !== null} onClick={handleIssueCreditNote}>
              {actionLoading === 'credit-note' ? 'Issuing...' : 'Issue Credit Note'}
            </Button>
          )}
          {canClose && (
            <Button variant="outlined" startIcon={<CheckCircle />} disabled={actionLoading !== null} onClick={handleClose}>
              {actionLoading === 'close' ? 'Closing...' : 'Close Return'}
            </Button>
          )}
          {canCancel && (
            <Button variant="outlined" color="error" startIcon={<Cancel />} disabled={actionLoading !== null} onClick={handleCancel}>
              {actionLoading === 'cancel' ? 'Cancelling...' : 'Cancel Return'}
            </Button>
          )}
          {noActionsAvailable && (
            <Typography variant="body2" color="text.secondary">
              This return is {salesReturn.status.toLowerCase().replace(/_/g, ' ')} -- no further actions are available.
            </Typography>
          )}
        </Box>
      </Paper>

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              Return Details
            </Typography>
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Customer
                </Typography>
                <Typography
                  variant="body2"
                  onClick={salesReturn.customer_id ? () => navigate(`/entity/customer/${salesReturn.customer_id}`) : undefined}
                  sx={{ fontWeight: 600, ...(salesReturn.customer_id ? { color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } } : {}) }}
                >
                  {salesReturn.customer_name || '—'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Invoice
                </Typography>
                {salesReturn.invoice_number ? (
                  <Typography
                    variant="body2"
                    onClick={salesReturn.invoice_id ? () => navigate(`/sales/invoices/${salesReturn.invoice_id}`) : undefined}
                    sx={{
                      fontWeight: 600,
                      ...(salesReturn.invoice_id ? { color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } } : {}),
                    }}
                  >
                    {salesReturn.invoice_number}
                  </Typography>
                ) : (
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>—</Typography>
                )}
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Invoice Total
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {salesReturn.invoice_total_amount != null ? formatIndianNumber(toNum(salesReturn.invoice_total_amount)) : '—'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Return Type
                </Typography>
                <TypePill label={salesReturn.return_type.replace(/_/g, ' ')} tint={TYPE_TINT[salesReturn.return_type] || '#64748b'} />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Restock
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {salesReturn.restock ? 'Yes' : 'No'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Warehouse
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {salesReturn.warehouse_id ? `#${salesReturn.warehouse_id}` : '—'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Requested By
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {salesReturn.created_by_name || '—'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Approved By
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {salesReturn.approved_by_name || '—'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Reason
                </Typography>
                <Typography variant="body2" color={salesReturn.reason ? 'text.primary' : 'text.disabled'} sx={{ whiteSpace: 'pre-wrap' }}>
                  {salesReturn.reason || 'No reason recorded.'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              Totals
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography color="text.secondary">Subtotal</Typography>
              <Typography fontWeight={600}>{formatIndianNumber(toNum(salesReturn.subtotal))}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography color="text.secondary">CGST</Typography>
              <Typography fontWeight={600}>{formatIndianNumber(toNum(salesReturn.cgst_amount))}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography color="text.secondary">SGST</Typography>
              <Typography fontWeight={600}>{formatIndianNumber(toNum(salesReturn.sgst_amount))}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography color="text.secondary">IGST</Typography>
              <Typography fontWeight={600}>{formatIndianNumber(toNum(salesReturn.igst_amount))}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Total
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {formatIndianNumber(toNum(salesReturn.total_amount))}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Items */}
        <Grid size={{ xs: 12 }}>
          <TableSurface>
            <Box sx={{ p: 2.5, pb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Returned Items
              </Typography>
            </Box>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Product', 'Qty', 'Unit Price', 'GST %', 'CGST', 'SGST', 'IGST', 'Line Total', 'Condition'].map((h, i) => (
                      <TableCell key={h} align={i >= 1 && i <= 7 ? 'right' : 'left'} sx={headerCellSx}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {salesReturn.items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} sx={{ border: 0 }}>
                        <TableEmptyState title="No items on this return" />
                      </TableCell>
                    </TableRow>
                  ) : (
                    salesReturn.items.map((item) => (
                      <TableRow key={item.id} sx={bodyRowSx}>
                        <TableCell sx={{ py: 1.25 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {item.product_name || '—'}
                          </Typography>
                          {(item.product_code || item.description) && (
                            <Typography variant="caption" color="text.secondary">
                              {[item.product_code, item.description].filter(Boolean).join(' · ')}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {toNum(item.quantity)} {item.product_uom || ''}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {formatIndianNumber(toNum(item.unit_price))}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {toNum(item.gst_rate)}%
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {formatIndianNumber(toNum(item.cgst_amount))}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {formatIndianNumber(toNum(item.sgst_amount))}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {formatIndianNumber(toNum(item.igst_amount))}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                          {formatIndianNumber(toNum(item.line_total))}
                        </TableCell>
                        <TableCell>
                          <StatusDot label={item.item_condition} tone={CONDITION_TONE[item.item_condition] || 'neutral'} />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <AttachmentsPanel entityType="sales_return" entityId={salesReturn.id} title="Return Documents" />
        </Grid>
      </Grid>
    </Box>
  );
};

export default SalesReturnDetailPage;
